import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useGameStore } from '../../store/game-store.js';
import { searchPerson, tmdb, imgUrl } from '../../lib/tmdb.js';
import { getRandomActors, getSeededRandomActors, getDailySeed, ACTOR_IDS } from '../../lib/actors.js';
import { MODES, DEFAULT_GAME_OPTIONS } from '../../lib/constants.js';
import { ALL_GENRES, DEFAULT_EXCLUDED_GENRES } from '../../lib/genres.js';
import { GameController } from '../../lib/game-controller.js';
import { loadData } from '../../lib/storage.js';
import './GameBuilder.css';

export default function GameBuilder() {
  const {
    targets, setTargets, addTarget, removeTarget,
    targetSlots, setTargetSlots,
    gameMode, gameOptions, updateGameOptions,
    setView, startGame, setLoading,
  } = useGameStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);
  const inputRef = useRef(null);

  const data = loadData();
  const isDaily = gameMode === MODES.DAILY;
  const isChain = gameMode === MODES.CHAIN_REACTION;

  // For Daily Challenge, auto-set targets
  useEffect(() => {
    if (isDaily) {
      const seed = getDailySeed();
      const actorIds = getSeededRandomActors(2, seed);
      fetchTargetsById(actorIds);
    }
  }, [isDaily]);

  const fetchTargetsById = async (ids) => {
    setLoading(true);
    try {
      const fetched = await Promise.all(
        ids.map(async (id) => {
          try {
            const data = await tmdb(`/person/${id}`);
            return {
              id: data.id,
              type: 'actor',
              name: data.name,
              image: imgUrl(data.profile_path, 'sm'),
              profile_path: data.profile_path,
            };
          } catch {
            return null;
          }
        })
      );
      setTargets(fetched.filter(Boolean));
    } catch (e) {
      console.error('Failed to fetch targets:', e);
    }
    setLoading(false);
  };

  // Debounced search
  const handleSearchChange = useCallback((e) => {
    const q = e.target.value;
    setSearchQuery(q);

    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (q.length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchPerson(q);
        setSearchResults(results.filter(r => r.profile_path).slice(0, 12));
      } catch {
        setSearchResults([]);
      }
      setSearching(false);
    }, 300);
  }, []);

  const handleSelectResult = (person) => {
    addTarget({
      id: person.id,
      type: 'actor',
      name: person.name,
      image: imgUrl(person.profile_path, 'sm'),
      profile_path: person.profile_path,
    });
    setSearchQuery('');
    setSearchResults([]);
    inputRef.current?.focus();
  };

  const handleShuffle = async () => {
    const count = targetSlots - targets.length;
    if (count <= 0) {
      // Replace all targets
      const ids = getRandomActors(targetSlots);
      await fetchTargetsById(ids);
    } else {
      // Fill empty slots
      const ids = getRandomActors(count);
      setLoading(true);
      try {
        const fetched = await Promise.all(
          ids.map(async (id) => {
            try {
              const data = await tmdb(`/person/${id}`);
              return {
                id: data.id,
                type: 'actor',
                name: data.name,
                image: imgUrl(data.profile_path, 'sm'),
                profile_path: data.profile_path,
              };
            } catch {
              return null;
            }
          })
        );
        setTargets([...targets, ...fetched.filter(Boolean)].slice(0, targetSlots));
      } catch (e) {
        console.error('Failed to shuffle:', e);
      }
      setLoading(false);
    }
  };

  const handleStartGame = async () => {
    if (targets.length < 2) return;

    setLoading(true);
    const controller = new GameController({
      targets: targets.map(t => ({ id: t.id, type: t.type })),
      excludedGenres: new Set(DEFAULT_EXCLUDED_GENRES),
      ...gameOptions,
    });

    startGame(controller);
  };

  const canStart = targets.length >= 2;

  return (
    <div className="builder">
      <header className="builder__header">
        <button className="builder__back" onClick={() => setView('home')}>
          &#x2190; Back
        </button>
        <h2 className="builder__title">
          {isDaily ? 'Daily Challenge' : isChain ? 'Chain Reaction' : gameMode === MODES.EXPLORATION ? 'Explore' : gameMode === MODES.TIME_ATTACK ? 'Time Attack' : gameMode === MODES.SUCCESSIVE_ROUNDS ? 'Successive Rounds' : 'Custom Game'}
        </h2>
      </header>

      {/* Target Selection */}
      <section className="builder__targets">
        <div className="builder__search-wrap">
          <input
            ref={inputRef}
            type="text"
            className="builder__search"
            placeholder="Search for an actor..."
            value={searchQuery}
            onChange={handleSearchChange}
            disabled={isDaily || targets.length >= targetSlots}
            autoFocus
          />
          {searching && <span className="builder__search-spinner" />}
        </div>

        {searchResults.length > 0 && (
          <div className="builder__results">
            {searchResults.map(person => (
              <button
                key={person.id}
                className="builder__result"
                onClick={() => handleSelectResult(person)}
                disabled={targets.some(t => t.id === person.id)}
              >
                {person.profile_path && (
                  <img
                    src={imgUrl(person.profile_path, 'sm')}
                    alt={person.name}
                    className="builder__result-img"
                    loading="lazy"
                  />
                )}
                <span className="builder__result-name">{person.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* Target Slots */}
        <div className="builder__slots">
          {targets.map((target, i) => (
            <div key={target.id} className="builder__slot builder__slot--filled">
              {target.image && (
                <img src={target.image} alt={target.name} className="builder__slot-img" />
              )}
              <span className="builder__slot-name">{target.name}</span>
              {!isDaily && (
                <button className="builder__slot-remove" onClick={() => removeTarget(i)}>
                  &#x2715;
                </button>
              )}
            </div>
          ))}
          {Array.from({ length: targetSlots - targets.length }).map((_, i) => (
            <div key={`empty-${i}`} className="builder__slot builder__slot--empty">
              <span className="builder__slot-placeholder">?</span>
            </div>
          ))}
        </div>

        {!isDaily && !isChain && (
          <div className="builder__slot-controls">
            <button
              className="builder__slot-btn"
              onClick={() => setTargetSlots(targetSlots - 1)}
              disabled={targetSlots <= 2}
            >-</button>
            <span className="builder__slot-count">{targetSlots} targets</span>
            <button
              className="builder__slot-btn"
              onClick={() => setTargetSlots(targetSlots + 1)}
              disabled={targetSlots >= 6}
            >+</button>
          </div>
        )}

        {!isDaily && (
          <button className="builder__shuffle" onClick={handleShuffle}>
            Shuffle
          </button>
        )}

        {/* Recent Targets */}
        {data.recentTargets.length > 0 && !isDaily && (
          <div className="builder__recent">
            <h4 className="builder__recent-title">Recent</h4>
            <div className="builder__recent-scroll">
              {data.recentTargets.slice(0, 10).map(id => (
                <button
                  key={id}
                  className="builder__recent-item"
                  onClick={() => fetchTargetsById([id])}
                  title={`Actor ID: ${id}`}
                >
                  {id}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Game Options (non-daily, non-chain) */}
      {!isDaily && !isChain && (
        <section className="builder__options">
          <h3 className="builder__options-title">Options</h3>

          <label className="builder__option">
            <span>Crew Connections</span>
            <input
              type="checkbox"
              checked={gameOptions.crewConnections}
              onChange={e => updateGameOptions({ crewConnections: e.target.checked })}
            />
          </label>

          <label className="builder__option">
            <span>Connection Preview</span>
            <input
              type="checkbox"
              checked={gameOptions.connectionPreview}
              onChange={e => updateGameOptions({ connectionPreview: e.target.checked })}
            />
          </label>

          {(gameMode === MODES.TIME_ATTACK || gameMode === MODES.SUCCESSIVE_ROUNDS) && (
            <label className="builder__option">
              <span>Time Limit (seconds)</span>
              <input
                type="number"
                className="builder__option-input"
                value={gameOptions.timeLimit || 120}
                min={30}
                max={600}
                onChange={e => updateGameOptions({ timeLimit: parseInt(e.target.value, 10) })}
              />
            </label>
          )}

          <label className="builder__option">
            <span>Hint Mode</span>
            <select
              className="builder__option-select"
              value={gameOptions.hintMode}
              onChange={e => updateGameOptions({ hintMode: e.target.value })}
            >
              <option value="unlimited">Unlimited</option>
              <option value="finite">Limited</option>
            </select>
          </label>

          {gameOptions.hintMode === 'finite' && (
            <label className="builder__option">
              <span>Hint Pool</span>
              <input
                type="number"
                className="builder__option-input"
                value={gameOptions.hintPool}
                min={1}
                max={20}
                onChange={e => updateGameOptions({ hintPool: parseInt(e.target.value, 10) })}
              />
            </label>
          )}
        </section>
      )}

      {/* Action Buttons */}
      <div className="builder__actions">
        <button
          className="builder__play"
          onClick={handleStartGame}
          disabled={!canStart}
        >
          Play
        </button>
        <button
          className="builder__play builder__play--friends"
          onClick={() => {
            // Multiplayer — create room (deferred)
            handleStartGame();
          }}
          disabled={!canStart}
        >
          Play with Friends
        </button>
      </div>
    </div>
  );
}
